import { PBInvoice, PBInvoiceAgent, PBInvoiceStatus, PBRecurringCharge } from "@/types/interfaces/billing";
import { ADMIN_ROUTE_PREFIX } from "@/types/constants/common";

export const BILLING_URL = `${ADMIN_ROUTE_PREFIX}/billing`;
export const AGENT_BILLING_URL = `${BILLING_URL}/agent`;
export const MONTHLY_BILLING_URL = `${BILLING_URL}/monthly`;
export const RECURRING_CHARGES_URL = `${BILLING_URL}/recurring-charges`;
export const BILLING_REPORTS_URL = `${BILLING_URL}/reports`;
// The invoices list, and /:id for one invoice
export const BILLING_INVOICE_URL = `${BILLING_URL}/invoices`;

export const agentBillingUrl = (email: string) => `${AGENT_BILLING_URL}?email=${encodeURIComponent(email)}`;

// Statuses where the agent still owes something
export const OPEN_STATUSES: PBInvoiceStatus[] = ["UNPAID", "PARTIALLY_PAID", "DECLINED"];

const round = (value: number) => Math.round(value * 100) / 100;

// `totalAmount` is before tax, so the invoice total adds the HST
export const invoiceTotal = (invoice: PBInvoice) => round((invoice.totalAmount || 0) + (invoice.totalHST || 0));

// Card payments are recorded as SALE, payments entered by hand as RECEIVED
const PAYMENT_TYPES = ["SALE", "RECEIVED"];

// Payments minus refunds recorded on the invoice. Invoices marked paid without a recorded payment (bank debits,
// older invoices) count as paid in full
export const invoicePaid = (invoice: PBInvoice) => {
  const transactions = invoice.transactions || [];
  const hasPayment = transactions.some((txn) => PAYMENT_TYPES.includes(String(txn.type)));
  if (!hasPayment && invoice.status === "PAID") return invoiceTotal(invoice);
  return round(
    transactions.reduce((sum, txn) => {
      const amount = Number(txn.amount) || 0;
      if (PAYMENT_TYPES.includes(String(txn.type))) return sum + amount;
      if (txn.type === "REFUND") return sum - amount;
      return sum;
    }, 0),
  );
};

// Money sent back on the invoice so far
export const invoiceRefunded = (invoice: PBInvoice) =>
  round(
    (invoice.transactions || [])
      .filter((txn) => txn.type === "REFUND")
      .reduce((sum, txn) => sum + (Number(txn.amount) || 0), 0),
  );

// Only invoices no money has come in on can be voided; the server checks the same
export const isVoidable = (invoice: PBInvoice) =>
  ["UNPAID", "DECLINED"].includes(invoice.status) &&
  !(invoice.transactions || []).some((txn) => PAYMENT_TYPES.includes(String(txn.type)));

export const invoiceBalance = (invoice: PBInvoice) =>
  OPEN_STATUSES.includes(invoice.status) ? Math.max(round(invoiceTotal(invoice) - invoicePaid(invoice)), 0) : 0;

// Unpaid or declined, with something to charge: $0 and credit invoices never go to Nuvei
export const isChargeable = (invoice: PBInvoice) =>
  (invoice.status === "UNPAID" || invoice.status === "DECLINED") && invoiceTotal(invoice) > 0;

// In an RBC file and still unpaid: the debit may still go through. Once RBC returns it, Mark declined ends it
export const hasPendingBankDebit = (invoice: PBInvoice) => !!invoice.rbcDebitOn && invoice.status === "UNPAID";

// Can go to Nuvei: chargeable, no bank debit pending, and nothing paid on it yet. The server checks the same
export const canChargeCard = (invoice: PBInvoice) =>
  isChargeable(invoice) && !hasPendingBankDebit(invoice) && invoicePaid(invoice) <= 0;

// What's left to refund to the card, as the server works it: the latest card sale less the refunds since, card or
// recorded by hand. Refunds before it belong to an earlier payment that was refunded before charging again
export const cardRefundable = (invoice: PBInvoice) => {
  const transactions = invoice.transactions || [];
  const saleIndex = transactions.reduce(
    (last, txn, index) => (txn.type === "SALE" && txn.gateway === "NUVEI" ? index : last),
    -1,
  );
  if (saleIndex < 0) return 0;
  const refunded = transactions
    .slice(saleIndex + 1)
    .filter((txn) => txn.type === "REFUND")
    .reduce((sum, txn) => sum + (Number(txn.amount) || 0), 0);
  return Math.max(round((Number(transactions[saleIndex].amount) || 0) - refunded), 0);
};

// Paid by card through Nuvei, so the payment can be refunded to the card
export const isCardRefundable = (invoice: PBInvoice) =>
  invoice.status === "PAID" &&
  (invoice.transactions || []).some((txn) => txn.type === "SALE" && txn.gateway === "NUVEI");

export type CollectionMethod = "CARD" | "BANK" | "MANUAL" | "OTHER";

// How the invoice is meant to be collected: the stored collectionMethod (set on create, backfilled Oct 2026),
// else the agent's current payment method. subCategory isn't used: NUVEI was stamped on everything for a while
export const collectionMethod = (invoice: PBInvoice & { agent?: PBInvoiceAgent }): CollectionMethod => {
  if (invoice.collectionMethod) return invoice.collectionMethod;
  const method = invoice.agent?.billing?.paymentMethod;
  if (method === "CREDIT_CARD") return "CARD";
  if (method === "BANK_WITHDRAWAL") return "BANK";
  if (method === "OTHER") return "MANUAL";
  return "OTHER";
};

export const COLLECTION_LABELS: Record<CollectionMethod, string> = {
  CARD: "Card",
  BANK: "Bank debit",
  MANUAL: "Manual",
  OTHER: "Other",
};

// What the invoice is for, from category and programYear
export const invoiceType = (invoice: PBInvoice) => {
  const year = invoice.programYear;
  switch (invoice.category) {
    case "MONTHLY_FEES":
      return "Monthly fees";
    case "CONFERENCE":
      return year ? `Conference ${year}` : "Conference";
    case "LUXE":
      return "Luxe";
    case "OTHER":
      return "One-off charge";
    default:
      return "Uncategorized";
  }
};

export interface SavedCard {
  label: string;
  expiry: string;
  expired: boolean;
}

// The agent's saved Nuvei card, the one charges go to
export const savedCard = (agent?: PBInvoiceAgent): SavedCard | null => {
  const card = agent?.nuvei?.[0]?.card;
  if (!card?.last4Digits) return null;
  const month = Number(card.ccExpMonth);
  const rawYear = Number(card.ccExpYear);
  const year = rawYear < 100 ? 2000 + rawYear : rawYear;
  // Cards work until the end of their expiry month
  const expired = !!month && !!year && new Date(year, month, 1) <= new Date();
  return {
    label: `${card.cardBrand || "Card"} ••${card.last4Digits}`,
    expiry: month && year ? `${String(month).padStart(2, "0")}/${String(year).slice(-2)}` : "",
    expired,
  };
};

// Only a missing card stops a charge. An expired one can still go through (issuers often keep honouring the saved
// card, or Nuvei's card updater has the new expiry), so finance may try it; it's flagged by cardProblem
export const hasNoCard = (invoice: PBInvoice & { agent?: PBInvoiceAgent }) =>
  !!invoice.agent && !savedCard(invoice.agent);

// What's wrong with the agent's card (missing or expired), or null when the card looks usable
export const cardProblem = (invoice: PBInvoice & { agent?: PBInvoiceAgent }) => {
  if (!invoice.agent) return null;
  const card = savedCard(invoice.agent);
  if (!card) return "No card on file";
  if (card.expired) return `Card expired ${card.expiry}`;
  return null;
};

// The latest payment or refund on the invoice, described for people. `paymentsOnly` skips refunds, for how a
// partly refunded invoice was paid
export const lastPayment = (invoice: PBInvoice, { paymentsOnly = false } = {}) => {
  const types = paymentsOnly ? PAYMENT_TYPES : [...PAYMENT_TYPES, "REFUND"];
  const txn = [...(invoice.transactions || [])].reverse().find((t) => types.includes(String(t.type)));
  if (!txn) return null;
  const byCard = txn.gateway === "NUVEI" || /CARD/i.test(txn.mode || "");
  const method = byCard ? "card" : txn.mode ? txn.mode.toLowerCase().replace(/_/g, " ") : "hand";
  return {
    isRefund: txn.type === "REFUND",
    method,
    amount: Number(txn.amount) || 0,
    date: txn.proccessedOn,
  };
};

// Whole days an open invoice is past its due date, or 0
export const daysOverdue = (invoice: PBInvoice) => {
  if (!OPEN_STATUSES.includes(invoice.status) || !invoice.dueOn) return 0;
  const days = Math.floor((Date.now() - new Date(invoice.dueOn).getTime()) / 86_400_000);
  return Math.max(days, 0);
};

// The card processor's reason for the latest declined charge, if it gave one
export const declineReason = (invoice: PBInvoice) => {
  const declined = [...(invoice.transactions || [])]
    .reverse()
    .find((t) => (t as { gatewayResponse?: { gwErrorReason?: string } }).gatewayResponse?.gwErrorReason);
  return (
    (declined as { gatewayResponse?: { gwErrorReason?: string } } | undefined)?.gatewayResponse?.gwErrorReason || ""
  );
};

// Distinct GL codes on the invoice's charges, which is how finance posts it
export const glCodes = (invoice: PBInvoice) =>
  Array.from(new Set((invoice.charges || []).map((c) => c.glCode).filter(Boolean))) as string[];

// A fixed-month charge that has been billed every month it was set up for. It stays active in the data, but the
// monthly run no longer bills it
export const isChargeComplete = (charge: PBRecurringCharge) =>
  !charge.occurrenceForever && (charge.occurrenceCompleted || 0) >= (charge.occurrenceCount || 0);

// Still being billed by the monthly run
export const isChargeBilling = (charge: PBRecurringCharge) => charge.active && !isChargeComplete(charge);

export const ATTENTION_OVERDUE_DAYS = 30;

// Why an open invoice needs finance to act, or null: a card invoice that can't be charged, a declined charge, or one
// long overdue. Credits never do
export const attentionReason = (invoice: PBInvoice & { agent?: PBInvoiceAgent }) => {
  if (!OPEN_STATUSES.includes(invoice.status) || invoiceTotal(invoice) <= 0) return null;
  const problem = collectionMethod(invoice) === "CARD" ? cardProblem(invoice) : null;
  if (problem) return problem;
  if (invoice.status === "DECLINED") return "Declined";
  const overdue = daysOverdue(invoice);
  return overdue >= ATTENTION_OVERDUE_DAYS ? `${overdue} days overdue` : null;
};
